import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { useLenis } from "lenis/react";
import { Menu, X } from "lucide-react";
import { LOGO } from "@/data";
import { useLang } from "@/context/LanguageContext";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { t, lang, setLang } = useLang();

  useLenis(({ scroll }) => setScrolled(scroll > 40));

  useEffect(() => {
    setScrolled(window.scrollY > 40);
  }, []);

  const LINKS = [
    { label: t.nav.about, to: "/#about" },
    { label: t.nav.services, to: "/#services" },
    { label: t.nav.journey, to: "/#journey" },
    { label: t.nav.why, to: "/#why" },
    { label: t.nav.faq, to: "/#faq" },
  ];

  return (
    <motion.nav
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-0 left-0 right-0 z-50"
      data-testid="navbar"
    >
      <div className={`transition-[background-color,backdrop-filter,border-color] duration-500 border-b ${scrolled || open ? "bg-[#F7F6F2]/90 backdrop-blur-xl border-[#E2E0D8]" : "bg-transparent border-transparent"}`}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link" onClick={() => setOpen(false)}>
            <img src={LOGO} alt="U-Heal" className="h-11 w-auto object-contain" />
          </Link>

          <div className="hidden lg:flex items-center gap-9">
            {LINKS.map((l) => (
              <Link key={l.to} to={l.to} className="text-sm font-medium text-[#111] relative group">
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#0D2A27] transition-[width] duration-300 group-hover:w-full" />
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center rounded-full border border-[#0D2A27]/20 overflow-hidden" data-testid="lang-toggle">
              {["en", "tr"].map((code) => (
                <button key={code} onClick={() => setLang(code)} data-testid={`lang-${code}`} className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors duration-300 ${lang === code ? "bg-[#0D2A27] text-[#F7F6F2]" : "text-[#0D2A27] hover:bg-[#0D2A27]/5"}`}>
                  {code}
                </button>
              ))}
            </div>
            <Link to="/#contact" data-testid="nav-cta-btn" className="group hidden sm:inline-flex items-center gap-2 rounded-full bg-[#0D2A27] px-6 py-3 text-sm font-semibold text-[#F7F6F2] transition-colors duration-300 hover:bg-[#163B37]">
              {t.nav.cta}
              <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
            </Link>
            <button onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-btn" aria-label="Menu" className="lg:hidden text-[#0D2A27] p-1">
              {open ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-[#F7F6F2]/95 backdrop-blur-xl border-b border-[#E2E0D8]"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {LINKS.map((l) => (
                <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="font-serif text-2xl text-[#0D2A27] py-2 border-b border-[#E2E0D8]/60">
                  {l.label}
                </Link>
              ))}
              <Link to="/#contact" onClick={() => setOpen(false)} data-testid="mobile-cta" className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-[#0D2A27] px-6 py-4 text-sm font-semibold text-[#F7F6F2]">
                {t.nav.cta} →
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};
